import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/SupabaseAuthContext';
import { apiFetch } from '../lib/api';
import { listPatients } from '../lib/patients';

// Fields marked with required: true are needed for PhenoAge calculation
const BLOOD_VALUE_FIELDS = [
  { key: 'albumin', label: 'Albumin', unit: 'g/dL', required: true },
  { key: 'creatinine', label: 'Creatinine', unit: 'mg/dL', required: true },
  { key: 'glucose', label: 'Glucose', unit: 'mg/dL', required: true },
  { key: 'hs_crp', label: 'hs-CRP', unit: 'mg/L', required: true },
  { key: 'lymphocyte_percent', label: 'Lymphocyte %', unit: '%', required: true },
  { key: 'mcv', label: 'MCV', unit: 'fL', required: true },
  { key: 'rdw', label: 'RDW', unit: '%', required: true },
  { key: 'alp', label: 'ALP', unit: 'U/L', required: true },
  { key: 'wbc', label: 'WBC', unit: 'K/uL', required: true },
  { key: 'bun', label: 'BUN', unit: 'mg/dL', required: false },
  { key: 'cholesterol_total', label: 'Cholesterol Total', unit: 'mg/dL', required: false },
  { key: 'cystatin_c', label: 'Cystatin C', unit: 'mg/L', required: false },
  { key: 'hba1c', label: 'HbA1c', unit: '%', required: false },
  { key: 'ggt', label: 'GGT', unit: 'U/L', required: false },
  { key: 'rbc', label: 'RBC', unit: 'M/uL', required: false },
  { key: 'monocyte_absolute', label: 'Monocyte Abs', unit: 'K/uL', required: false },
  { key: 'neutrophil_absolute', label: 'Neutrophil Abs', unit: 'K/uL', required: false },
  { key: 'alt', label: 'ALT', unit: 'U/L', required: false },
  { key: 'shbg', label: 'SHBG', unit: 'nmol/L', required: false },
  { key: 'vitamin_d', label: 'Vitamin D', unit: 'ng/mL', required: false },
  { key: 'mch', label: 'MCH', unit: 'pg', required: false },
  { key: 'apolipoprotein_a1', label: 'ApoA1', unit: 'mg/dL', required: false },
];

export const UploadPage = () => {
  const { organization } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedPatient = searchParams.get('patient');

  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(preselectedPatient || '');
  const [collectionDate, setCollectionDate] = useState('');
  const [activeTab, setActiveTab] = useState('upload');
  
  // Upload state
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null);

  // Manual entry state
  const [bloodValues, setBloodValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchPatients = useCallback(async () => {
    try {
      const data = await listPatients(organization.id);
      setPatients(data);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    }
  }, [organization.id]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer?.files?.[0] || e.target.files?.[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setUploadStatus(null);
    } else {
      toast.error('Please upload a PDF file');
    }
  }, []);

  const handleUpload = async () => {
    if (!file || !selectedPatient) {
      toast.error('Please select a patient and a PDF file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('patient_id', selectedPatient);
    // Optional: if omitted, the backend reads the collection date off the PDF.
    if (collectionDate) {
      formData.append('collection_date', collectionDate);
    }

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 500);

      const response = await apiFetch('/analyze/upload', {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const result = await response.json();
        setUploadStatus('success');
        const extraction = result.extraction || null;
        const needsReview = extraction?.recommend_review;
        if (needsReview) {
          toast.warning('Analysis saved — please confirm the highlighted values');
        } else {
          toast.success('Blood work analyzed successfully');
        }
        setTimeout(() => {
          navigate(`/analyses/${result.analysis_id}`, {
            state: extraction ? { extraction } : undefined,
          });
        }, needsReview ? 600 : 1500);
      } else {
        const error = await response.json();
        setUploadStatus('error');
        toast.error(error.detail || 'Analysis failed');
      }
    } catch (error) {
      setUploadStatus('error');
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!selectedPatient || !collectionDate) {
      toast.error('Please select a patient and collection date');
      return;
    }

    const hasValues = Object.values(bloodValues).some(v => v !== '' && v !== null);
    if (!hasValues) {
      toast.error('Please enter at least some blood values');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiFetch('/analyze/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: selectedPatient,
          collection_date: collectionDate,
          blood_values: bloodValues,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Analysis completed successfully');
        navigate(`/analyses/${result.analysis_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Analysis failed');
      }
    } catch (error) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const updateBloodValue = (key, value) => {
    setBloodValues(prev => ({
      ...prev,
      [key]: value === '' ? null : parseFloat(value),
    }));
  };

  return (
    <div className="space-y-6" data-testid="upload-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">
          Upload Blood Work
        </h1>
        <p className="text-slate-600 mt-1">
          Upload a PDF or enter values manually for biological age analysis.
        </p>
      </div>

      {/* Patient & Date Selection */}
      <Card className="bg-white border-slate-200 shadow-soft">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Select Patient *</Label>
              <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                <SelectTrigger data-testid="select-patient">
                  <SelectValue placeholder="Choose a patient" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map((patient) => (
                    <SelectItem key={patient.patient_id} value={patient.patient_id}>
                      {patient.first_name} {patient.last_name} ({patient.patient_id_number})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {patients.length === 0 && (
                <p className="text-sm text-slate-500">
                  No patients found.{' '}
                  <a href="/patients" className="text-[#196a70] hover:underline">
                    Add a patient first
                  </a>
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Collection Date{activeTab === 'manual' ? ' *' : ''}</Label>
              <Input
                type="date"
                value={collectionDate}
                onChange={(e) => setCollectionDate(e.target.value)}
                data-testid="collection-date"
              />
              {activeTab === 'upload' && (
                <p className="text-sm text-slate-500">
                  Leave blank to auto-detect the date from the PDF.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload/Manual Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger
            value="upload"
            className="data-[state=active]:bg-white data-[state=active]:text-[#196a70]"
            data-testid="tab-upload"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload PDF
          </TabsTrigger>
          <TabsTrigger
            value="manual"
            className="data-[state=active]:bg-white data-[state=active]:text-[#196a70]"
            data-testid="tab-manual"
          >
            <Plus className="w-4 h-4 mr-2" />
            Manual Entry
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="mt-6">
          <Card className="bg-white border-slate-200 shadow-soft">
            <CardContent className="p-6">
              {/* Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                  file
                    ? 'border-[#196a70] bg-[#dbe9e5]/30'
                    : 'border-slate-300 hover:border-[#196a70] hover:bg-slate-50'
                }`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
              >
                {file ? (
                  <div className="space-y-4">
                    <div className="w-16 h-16 rounded-full bg-[#dbe9e5] flex items-center justify-center mx-auto">
                      <FileText className="w-8 h-8 text-[#196a70]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{file.name}</p>
                      <p className="text-sm text-slate-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFile(null);
                        setUploadStatus(null);
                      }}
                      className="text-slate-500"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                      <Upload className="w-8 h-8 text-slate-400" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        Drop your PDF here, or{' '}
                        <label className="text-[#196a70] hover:underline cursor-pointer">
                          browse
                          <input
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            onChange={handleFileDrop}
                            data-testid="file-input"
                          />
                        </label>
                      </p>
                      <p className="text-sm text-slate-500">PDF files only</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="mt-6 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">
                      <Loader2 className="w-4 h-4 inline-block mr-2 animate-spin" />
                      Analyzing blood work with AI...
                    </span>
                    <span className="font-mono text-slate-900">{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                </div>
              )}

              {/* Upload Status */}
              {uploadStatus === 'success' && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-green-800">
                    Analysis complete! Redirecting to results...
                  </span>
                </div>
              )}

              {uploadStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <span className="text-red-800">
                    Analysis failed. Please try again or enter values manually.
                  </span>
                </div>
              )}

              {/* Upload Button */}
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={handleUpload}
                  disabled={!file || !selectedPatient || uploading}
                  className="bg-[#196a70] hover:bg-[#12565b] text-white"
                  data-testid="analyze-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Analyze Blood Work
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="manual" className="mt-6">
          <Card className="bg-white border-slate-200 shadow-soft">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg">
                Enter Blood Work Values
              </CardTitle>
              <p className="text-sm text-slate-500">
                Fields marked with <span className="text-[#196a70] font-medium">*</span> are required for PhenoAge. All 22 fields needed for Bortz Age.
              </p>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {BLOOD_VALUE_FIELDS.map((field) => (
                  <div key={field.key} className="space-y-1">
                    <Label className={`text-xs ${field.required ? 'text-[#196a70] font-medium' : 'text-slate-600'}`}>
                      {field.label}{field.required && ' *'}
                      <span className="text-slate-400 ml-1 font-normal">({field.unit})</span>
                    </Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="—"
                      value={bloodValues[field.key] || ''}
                      onChange={(e) => updateBloodValue(field.key, e.target.value)}
                      className={`h-9 text-sm ${field.required ? 'border-[#196a70]/30' : ''}`}
                      data-testid={`blood-${field.key}`}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end">
                <Button
                  onClick={handleManualSubmit}
                  disabled={!selectedPatient || !collectionDate || submitting}
                  className="bg-[#196a70] hover:bg-[#12565b] text-white"
                  data-testid="manual-analyze-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    'Calculate Biological Age'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
